/**
 * features/auth/Login.tsx
 * ------------------------------------------------------------------
 * Split-screen sign-in (the LKC pattern, Acepsle-styled):
 *   - Left: a bold ORANGE brand panel — reversed wordmark, tagline,
 *     and value-prop chips. Solid colour with faint decorative blurred
 *     shapes (no gradients). This is the warm, colourful, friendly
 *     presence (FreshBooks / Wave energy).
 *   - Right: the actual sign-in — Google first (the operator email is
 *     a Google Workspace account), then email/password with a
 *     sign-in / sign-up toggle.
 * Stacks vertically on small screens.
 * ------------------------------------------------------------------
 */
import { useState } from 'react';
import {
  Receipt,
  Wallet,
  FileSignature,
  ShieldCheck,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Loader2,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import AcepsleWordmark from '@/components/branding/AcepsleWordmark';

type Mode = 'signin' | 'signup';

const FEATURES = [
  { icon: Receipt, label: 'Invoices & receipts' },
  { icon: Wallet, label: 'Payments & statements' },
  { icon: FileSignature, label: 'Quotes & expenses' },
  { icon: ShieldCheck, label: 'BURS-ready records' },
];

/** Inline Google "G" mark (no remote asset). */
function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.89 2.68-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.83.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  );
}

export function Login({ onBack }: { onBack?: () => void }) {
  const { signIn, signUp, signInWithGoogle } = useAuth();
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function describeError(code: string): string {
    switch (code) {
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
        return 'Incorrect email or password.';
      case 'auth/user-not-found':
        return 'No account found for that email.';
      case 'auth/email-already-in-use':
        return 'An account already exists for that email. Try signing in.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please wait a moment and try again.';
      case 'auth/popup-closed-by-user':
        return 'Google sign-in was cancelled.';
      case 'auth/popup-blocked':
        return 'Your browser blocked the Google popup. Allow popups and retry.';
      case 'auth/operation-not-allowed':
        return 'This sign-in method is not enabled in Firebase yet.';
      default:
        return 'Something went wrong. Please try again.';
    }
  }

  async function handleEmail() {
    setError(null);
    if (mode === 'signup' && password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'signin') await signIn(email, password);
      else await signUp(email, password);
    } catch (err) {
      setError(describeError((err as { code?: string }).code ?? ''));
    } finally {
      setBusy(false);
    }
  }

  async function handleGoogle() {
    setError(null);
    setBusy(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(describeError((err as { code?: string }).code ?? ''));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      {/* ── Brand panel ──────────────────────────────────────── */}
      <div className="relative flex flex-col justify-center overflow-hidden bg-brand px-8 py-12 text-white lg:w-1/2 lg:px-14 lg:py-16">
        {/* decorative solid blurred shapes (no gradients) */}
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-20 h-[26rem] w-[26rem] rounded-full bg-brandHover/40 blur-3xl" />

        <div className="relative z-10 mx-auto w-full max-w-md">
          <AcepsleWordmark height={40} variant="reversed" />

          <div className="mt-10">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-sm font-medium backdrop-blur-sm">
              <span className="h-2 w-2 rounded-full bg-white" />
              Business management hub
            </span>
          </div>

          <h2 className="mt-6 text-3xl font-bold leading-tight">
            Invoicing, payments &amp; compliance — in one place.
          </h2>
          <p className="mt-3 text-base leading-relaxed text-white/85">
            Raise invoices and receipts, record payments, send quotes, and keep
            tax-ready records for Acepsle Proprietary Limited.
          </p>

          <div className="mt-8 grid grid-cols-2 gap-3">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.label}
                  className="flex items-center gap-2.5 rounded-xl border border-white/15 bg-white/10 px-3 py-2.5"
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/15">
                    <Icon size={15} />
                  </div>
                  <span className="text-xs leading-tight text-white/90">
                    {f.label}
                  </span>
                </div>
              );
            })}
          </div>

          <p className="mt-10 text-xs text-white/50">
            Plot 37043 Block 8, Gaborone · info@acepslebotswana.com
          </p>
        </div>
      </div>

      {/* ── Sign-in panel ────────────────────────────────────── */}
      <div className="flex flex-1 items-center justify-center bg-surface px-6 py-12 dark:bg-surfaceDark lg:w-1/2">
        <div className="w-full max-w-sm">
          {onBack && (
            <button
              onClick={onBack}
              className="mb-4 text-sm font-semibold text-primaryLight transition hover:text-brand dark:text-gray-400"
            >
              ← Back to home
            </button>
          )}
          <h1 className="text-2xl font-bold text-primary dark:text-white">
            {mode === 'signin' ? 'Welcome back' : 'Create your account'}
          </h1>
          <p className="mt-1 text-sm text-primaryLight dark:text-gray-400">
            {mode === 'signin'
              ? 'Sign in to the Acepsle Hub.'
              : 'Set up access to the Acepsle Hub.'}
          </p>

          {/* Google — primary */}
          <button
            onClick={() => void handleGoogle()}
            disabled={busy}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-none border border-black/10 bg-white px-4 py-2.5 text-sm font-semibold text-primary transition hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:bg-white/5 dark:text-gray-100 dark:hover:bg-white/10"
          >
            <GoogleG />
            Continue with Google
          </button>

          <div className="my-5 flex items-center gap-3">
            <span className="h-px flex-1 bg-black/10 dark:bg-white/10" />
            <span className="text-xs uppercase tracking-wide text-gray-400">
              or
            </span>
            <span className="h-px flex-1 bg-black/10 dark:bg-white/10" />
          </div>

          <div className="space-y-4">
            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-primaryLight dark:text-gray-400">
                Email
              </span>
              <div className="relative">
                <Mail
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="email"
                  value={email}
                  autoComplete="username"
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && void handleEmail()}
                  placeholder="info@acepslebotswana.com"
                  className="w-full rounded-xl border border-black/10 bg-white py-2.5 pl-9 pr-3 text-sm text-ink shadow-sm transition placeholder:text-gray-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/25 dark:border-white/10 dark:bg-white/5 dark:text-gray-100"
                />
              </div>
            </label>

            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-primaryLight dark:text-gray-400">
                Password
              </span>
              <div className="relative">
                <Lock
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  autoComplete={
                    mode === 'signin' ? 'current-password' : 'new-password'
                  }
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && void handleEmail()}
                  placeholder="••••••••"
                  className="w-full rounded-xl border border-black/10 bg-white py-2.5 pl-9 pr-10 text-sm text-ink shadow-sm transition placeholder:text-gray-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/25 dark:border-white/10 dark:bg-white/5 dark:text-gray-100"
                />
                <button
                  type="button"
                  aria-label="Press and hold to reveal password"
                  title="Press and hold to reveal"
                  onMouseDown={() => setShowPw(true)}
                  onMouseUp={() => setShowPw(false)}
                  onMouseLeave={() => setShowPw(false)}
                  onTouchStart={() => setShowPw(true)}
                  onTouchEnd={() => setShowPw(false)}
                  onBlur={() => setShowPw(false)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 select-none rounded-lg p-1.5 text-gray-400 transition hover:bg-black/5 hover:text-gray-700 dark:hover:bg-white/10 dark:hover:text-gray-200"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </label>

            {mode === 'signup' && (
              <label className="block">
                <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-primaryLight dark:text-gray-400">
                  Confirm password
                </span>
                <div className="relative">
                  <Lock
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="password"
                    value={confirm}
                    autoComplete="new-password"
                    onChange={(e) => setConfirm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && void handleEmail()}
                    placeholder="••••••••"
                    className="w-full rounded-xl border border-black/10 bg-white py-2.5 pl-9 pr-3 text-sm text-ink shadow-sm transition placeholder:text-gray-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/25 dark:border-white/10 dark:bg-white/5 dark:text-gray-100"
                  />
                </div>
              </label>
            )}

            {error && (
              <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">
                {error}
              </div>
            )}

            {/* SHARP orange CTA — the signature */}
            <button
              onClick={() => void handleEmail()}
              disabled={busy || !email || !password}
              className="flex w-full items-center justify-center gap-2 rounded-none bg-brand px-4 py-3 text-sm font-semibold text-white shadow-card transition hover:bg-brandHover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy && <Loader2 size={16} className="animate-spin" />}
              {mode === 'signin' ? 'Sign in' : 'Create account'}
            </button>
          </div>

          <div className="mt-5 text-center text-sm text-gray-500 dark:text-gray-400">
            {mode === 'signin' ? (
              <>
                Need an account?{' '}
                <button
                  onClick={() => {
                    setMode('signup');
                    setError(null);
                  }}
                  className="font-semibold text-brand hover:underline"
                >
                  Create one
                </button>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <button
                  onClick={() => {
                    setMode('signin');
                    setError(null);
                  }}
                  className="font-semibold text-brand hover:underline"
                >
                  Sign in
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
